import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@repo/ui';
import { BadgeCheck } from 'lucide-react';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { PoweredByVera } from 'components/UI';
import { TermInfo } from 'components/UI/TermInfo';
import { RouteDiagram } from './RouteDiagram';
import { VERIFY_POSSESSION_COPY } from 'pages/CertificatePage/components/VerifyPossessionCard';

interface AuthenticityDrawerProps {
  open: boolean;
  onClose: () => void;
  /**
   * Whether this render was reached via a genuine tag scan (a valid `vt`).
   *
   * VRG-252 — accepts the three settled verdicts as well as the original boolean. `connected` is
   * a Connect (NTAG 213) tap: a tag WAS present, and its silicon carries no challenge to answer.
   */
  verified: boolean | 'verified' | 'connected' | 'unverified';
  /**
   * VERA-644 (TD-066): white-label entitlement, threaded through from the same
   * source the Footer uses. Without it this drawer would show "Powered by Vera"
   * to a brand that has paid to remove exactly that, while their footer hid it.
   */
  hidePoweredBy?: boolean;
  /**
   * VERA-304 — whether the OWNERSHIP record still has an evidence level to earn.
   *
   * Must be the caller's `canVerifyPossession`, verbatim: this is the second route
   * to the same action the `Verify` tile offers, and a gate that differs from the
   * tile's would let one surface advertise something the other has withdrawn.
   *
   * ⚠️ THIS IS NOT THE VERDICT THE REST OF THE DRAWER IS ABOUT, and the copy below
   * is written to keep the two apart. `verified` is how THIS PAGE VIEW was reached
   * (a tag tap in this session, or a link); this is whether the owner has ever
   * proven the item is in their hands. They move independently: an owner who taps
   * the tag sees "Authentic" while their transfer is still only `claimed`, and
   * verifying possession does NOT change the badge on the photograph. So the block
   * below never promises the badge will flip, and it does not continue the
   * unverified branch's "tap the tag and it will show as Authentic" sentence.
   */
  canVerifyPossession?: boolean;
  /**
   * VERA-304 — opens the possession scan. The caller is expected to CLOSE this
   * drawer in the same handler (see CertificatePage): the scan is a task and this
   * is an explainer, so it replaces it rather than stacking on top of it.
   */
  onVerifyPossession?: () => void;
}

/**
 * Explains what the Authentic / Unverified badge actually means (VRG-240).
 *
 * Content order is deliberate. Someone who just tapped a tag wants to know what the
 * badge says about THIS item first; "how authentication works" is the follow-up, not
 * the lead. So the first paragraph is always about their object, and the general
 * explainer sits underneath it.
 *
 * It also gives the Unverified state somewhere to explain itself. That state is easy
 * to misread as an accusation ("this is fake") when the ordinary cause is a shared or
 * bookmarked link, so the copy names that cause first and gives a concrete way to get
 * a real answer.
 *
 * TITLE MATCHES THE BADGE — and has to be kept in step with it. These have now
 * disagreed twice, in both directions: first the drawer said "Not verified" while the
 * chip said "Unverified", then the chip was renamed and the drawer was left behind.
 * Two names for one state, side by side on the same screen, read as two different
 * things. If AuthenticChip's label changes, change it here in the same commit.
 *
 * ⚠️ VRG-252 — THE "HOW AUTHENTICATION WORKS" BLOCK WAS THE BUG, more than the title was.
 * It stated, unconditionally, that "each item carries a secure chip with its own key" and
 * "every tap creates a fresh, single-use proof, so a tag cannot be cloned or replayed" —
 * which is precisely, specifically false for a Connect tag, whose chip has no key and no
 * proof. Shipping that paragraph under a Connect tap would not merely have overstated the
 * badge; it would have told the reader the exact opposite of the one thing they most need
 * to know about the tag in their hand. It is now branched, and the Connect version names
 * the ceiling in plain words: the tag can be sealed so nobody rewrites it, and its address
 * can still be copied onto a different tag. That is tamper-resistant and not
 * tamper-evident, and it is why Connect is a different method rather than a weaker one.
 *
 * The two quotes above are the ORIGINAL wording, kept verbatim so the bug stays legible.
 * The non-Connect branch has since been reworded ("each item"/"chip" -> "this item"/"tag")
 * for the reasons given at that call site — do not read them as current copy.
 *
 * TYPE SIZE IS SET EXPLICITLY, not left to the variant. `body2` resolves to 12px in
 * apps/app (its theme overrides the 16px in @repo/ui's scale), which is below what
 * body copy should be on a phone — and this is a phone surface by definition, since
 * you arrive by tapping a tag with one. Sizes here are hard numbers so the drawer
 * cannot silently shrink again if the app's variant scale moves.
 *
 * ⚠️ Copy is PROVISIONAL and wants a product/legal read before it ships. It makes
 * claims about what verification proves, and the honest bound is narrow: a valid token
 * proves this render followed a genuine tag challenge, not that the object in someone's
 * hand is the tagged one.
 *
 * NOTE: no "verified N seconds ago" line. /verify/resolve returns only
 * `{ verified, certificateId }` -- there is no timestamp to render, and inventing a
 * freshness claim the API cannot support would be worse than omitting it.
 */
export const AuthenticityDrawer: React.FC<AuthenticityDrawerProps> = ({
  open,
  onClose,
  verified,
  hidePoweredBy,
  canVerifyPossession = false,
  onVerifyPossession,
}) => {
  const state: 'verified' | 'connected' | 'unverified' =
    verified === true ? 'verified' : verified === false ? 'unverified' : verified;
  const isVerified = state === 'verified';
  const isConnected = state === 'connected';
  // The ROUTE, not the verdict (see RouteDiagram). A Connect tap took the tag route, so it draws
  // the tag route — the diagram has no tick and asserts nothing about authenticity, which is
  // exactly why it can be shared between the two tapped states without overstating one of them.
  const tookTagRoute = isVerified || isConnected;

  /**
   * "Vera tag" or plain "tag", decided by the SAME entitlement that governs the
   * attribution at the foot of this drawer.
   *
   * White label is a paid enterprise entitlement (VERA-644 / TD-066) under which
   * Vera's name "must not render AT ALL". `PoweredByVera` enforces that by
   * refusing to render ITSELF rather than trusting call sites — a decision taken
   * after this very drawer became a second surface carrying the attribution and
   * leaked it while the page footer correctly hid it.
   *
   * ⚠️ That guard cannot see a brand name welded into a SENTENCE. This body copy
   * said "Vera tag" four times, unconditionally, so a white-labelled brand's own
   * customers read Vera's name in the paragraph directly above an attribution
   * block deliberately suppressed for them. A component can refuse to render; a
   * substring cannot. So the noun is derived here, from the flag the drawer is
   * already given, rather than left as a literal.
   *
   * The generic form loses nothing the reader needs: every one of these sentences
   * is about the physical tag on the item in their hand, and "the tag" identifies
   * it unambiguously in context.
   */
  const tagNoun = hidePoweredBy ? 'tag' : 'Vera tag';

  return (
  <DrawerPopup
    title={isVerified ? 'Authentic' : isConnected ? 'Tag scanned' : 'Not verified'}
    handleOpen={open}
    handleClose={onClose}
    child={
      <Stack spacing={4} sx={{ pb: 1 }}>
        {/* Above the copy, not below it: this is the orientation, not a footnote.
            Someone opens this drawer to find out what the badge means, and the route
            is the answer — the paragraphs are the elaboration. */}
        {/* `key` on `open` REMOUNTS the diagram each time the drawer opens, which
            restarts its animation from the beginning (RouteDiagram).

            Still needed now that the pulse loops rather than running once, for a
            different reason: MUI keeps a Drawer's children mounted across
            close/open, so the loop would keep running unseen while the drawer is
            shut and a reopen would catch it at an arbitrary point in the cycle —
            often mid-rest, so the first thing a returning reader sees is a still
            picture. Remounting makes the diagram's opening delay mean what it says:
            the drawer settles, then the pulse sets off. */}
        <RouteDiagram key={open ? 'open' : 'closed'} viaTag={tookTagRoute} />

        <Typography sx={{ fontSize: 16, lineHeight: 1.5 }}>
          {isVerified
            ? `You reached this certificate by tapping its ${tagNoun}. The tag answered a security challenge that only the genuine tag can answer, so this page is showing the real record for that tag.`
            : isConnected
              ? `You reached this certificate by tapping the ${tagNoun} on this item. This kind of tag is sealed, so the address it carries cannot be changed once it leaves the brand.`
              : `You reached this certificate from a link rather than by tapping its ${tagNoun}. That is normal for a shared or saved link. It just means we cannot confirm the physical tag was present.`}
        </Typography>

        <Typography sx={{ fontSize: 16, lineHeight: 1.5 }}>
          {isVerified
            ? 'A copied link cannot produce that answer, so it cannot show this state.'
            : isConnected
              ? // The honest ceiling, stated plainly rather than hedged. A reader who is told a tag
                // is "sealed" will reasonably assume that settles authenticity, so the very next
                // sentence has to say what it does not settle — and it names the brand as the
                // route to a real answer, because there is nothing the reader can do themselves.
                'It does not tell us that this is the original tag. This tag does not carry the security challenge that proves that, so the same address could have been copied onto another tag. If you have any doubt about the item, contact the brand.'
              : `How to verify: tap the ${tagNoun} on the item with your phone. The certificate will then show as Authentic.`}
        </Typography>

        <Box>
          {/*
            VRG-426 — the `(i)` beside this heading, and ONLY on this branch.

            The five paragraphs above and below are the app's longest hand-written
            account of what verification is, and this component's own docblock already
            records the cost of that: it carries a standing manual-sync obligation with
            `AuthenticChip` ("if AuthenticChip's label changes, change it here in the
            same commit") which has been broken twice, in both directions. §44 exists
            for exactly that shape — a definition retyped at a call site with nothing
            comparing it to the source.

            The prose STAYS. It is longer-form explanation with a product/legal read
            pending (see the docblock), and it says things a 180-character `short`
            cannot. The glossary entry is the short, canonical answer to "what does
            verify mean", offered beside the heading; the paragraphs are the elaboration.

            Not on the CONNECT branch. There the heading is "What this tag does" and the
            section's point is that this tag CANNOT verify — a glyph labelled "What does
            'Verify' mean?" beside a heading that never says the word would be answering
            a question the reader did not ask on that screen.
          */}
          <Stack direction='row' alignItems='center' spacing={0.5} sx={{ pb: 1.5 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, display: 'block' }}>
              {isConnected ? 'What this tag does' : 'How authentication works'}
            </Typography>
            {!isConnected && <TermInfo termId='verify' />}
          </Stack>
          <Typography color='text.secondary' sx={{ fontSize: 16, lineHeight: 1.5 }}>
            {isConnected
              ? "This tag's job is to open the item's certificate on every tap, and to be sealed so nobody can change where it points. It records the item's details and its history of ownership and scans. Brands can also choose tags that prove the tag itself on every tap."
              : // "THIS item", not "each item": the reader opened this drawer about the
                // certificate in front of them, and a sentence about the general population
                // of items answers a question they did not ask.
                //
                // "TAG", not "chip" — and this is a standing product rule, not a preference.
                // The chip is internal fulfilment detail (NTAG 424 DNA and the rest of the
                // family list); the consumer-facing noun is "tag". It was also an internal
                // inconsistency: every other sentence in this drawer already says tag,
                // including the one directly above it telling the reader to "tap the Vera tag".
                //
                // "so THE tag": the sentence before now names a specific tag, so continuing
                // with the generic "a tag" reads as a switch back to tags-in-general.
                "This item carries a secure tag with its own verifiable key. Every tap creates a fresh, single-use proof, so the tag cannot be cloned or replayed. The certificate records the item's details and its history of ownership and scans."}
          </Typography>
        </Box>

        {/*
          VERA-304 — THE REMEDY, BESIDE THE STATE THAT SENT YOU LOOKING FOR IT.

          The badge is the only thing on the page that reports on verification, and
          until now the only thing you could DO about it lived in a row of tiles
          further down, among things you may optionally do (Gift, Sell, Report).
          Someone who reads the badge has no reason to go hunting there. So the
          action is offered here, one tap from the state, and the tile stays as the
          second route for someone browsing actions rather than reading a status.

          IT IS A SEPARATE BLOCK, not a continuation of the paragraphs above, and
          the rule border is doing real work rather than decoration. Everything
          above answers "what does this badge mean"; this answers "what can I do
          about my ownership record", which is a different question about a
          different fact (see `canVerifyPossession`). Run together, the button would
          read as the way to make the badge say Authentic, which it is not.

          Shown on ALL THREE verdict branches, because the gate is the ownership
          record and not the badge. An owner who tapped the tag this session sees
          "Authentic" above and may still have a `claimed` transfer to upgrade.
        */}
        {canVerifyPossession && onVerifyPossession && (
          <Box sx={{ pt: 1, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
            <Typography sx={{ fontSize: 16, fontWeight: 600, display: 'block', pt: 2, pb: 1 }}>
              Confirm you have this item
            </Typography>
            <Typography color='text.secondary' sx={{ fontSize: 16, lineHeight: 1.5, pb: 2.5 }}>
              {/* One sentence, one source — the state card says exactly this, and two
                  copies is how they drift (§36/§44). */}
              {VERIFY_POSSESSION_COPY}
            </Typography>
            <Button
              variant='primary'
              sx={{ width: '100%', paddingX: 2 }}
              startIcon={<BadgeCheck size={16} />}
              onClick={onVerifyPossession}
            >
              Verify possession
            </Button>
          </Box>
        )}

        {/* Attribution sits at the FOOT, after the explanation, not in the header.
            This drawer is shown inside a brand's own certificate — leading with our
            name would put Vera above the brand on their surface. Trailing it reads as
            a signature on the guarantee just made, which is the honest placement:
            the claim is ours, so the name belongs against the claim. */}
        <Stack direction='row' justifyContent='center' sx={{ pt: 2 }}>
          <PoweredByVera hidden={hidePoweredBy} />
        </Stack>
      </Stack>
    }
  />
  );
};

export default AuthenticityDrawer;
